import axios, { CancelTokenSource } from 'axios';
import {
  Chart as ChartModel,
  VolumeGraph as VolumeGraphModel,
  MultiGraphInstance,
  Co2Chart as Co2ChartModel,
  Co2ChartInstance,
  Material,
  MixedChartInstance,
  MaterialInstance,
  CircularGraph,
  CircularChartInstance,
} from '../model';
import { AbstractBCMPortal } from './abstractBCMPortal';
import { ExportAllDataInterface, ExportDataInterface } from '../../components/Button/Export/AllSerializer';
import { ExportMovementDataInterface } from '../../components/Button/Export/MovementSerializer';
import { ChartRestInterface } from '../ChartInterface';

const CO2 = '/co2';
const REVENUES = '/revenues';
const VOLUMES = '/volumes';
const CIRCULAR_ECONOMY = '/circular_economy';
const MATERIALS = '/materials';
const INDEXES = '/indexes';

let cancelTokenRevenues: CancelTokenSource
let cancelTokenMaterials: CancelTokenSource
let cancelTokenVolumes: CancelTokenSource
let cancelTokenCircularEconomy: CancelTokenSource
let cancelTokenMovementVolume: CancelTokenSource
let cancelTokenCo2: CancelTokenSource
let cancelTokenMovementCo2: CancelTokenSource
let cancelTokenMixed: CancelTokenSource
export class Chart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';
  model = new ChartModel();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: MultiGraphInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenRevenues !== undefined) {
      cancelTokenRevenues.cancel('Operation RevenusChart canceled due to new request.')
    }
      
    cancelTokenRevenues = axios.CancelToken.source();

    return this.request()
      .get(this.getUrl(REVENUES, true),{ cancelToken: cancelTokenRevenues?.token })
      .then(({ data }) => {
        return {data: this.model.getMany(data?.data || data), ...rest}
      });
  };
}
export class FilterChart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';
  model = new ChartModel();

  getMaterials = (
    rest?: ChartRestInterface
  ): Promise<
    {
      materials: MaterialInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenMaterials !== undefined) {
      cancelTokenMaterials.cancel('Operation FilterChart canceled due to new request.')
    }
      
    cancelTokenMaterials = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(MATERIALS, true), { cancelToken: cancelTokenMaterials?.token }) 
      .then(({ data }) => ({
        materials: new Material().getMany(data),
        ...rest,
      }));
  };
}

export class VolumeChart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';
  model = new VolumeGraphModel();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: MultiGraphInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenVolumes !== undefined) {
      cancelTokenVolumes.cancel('Operation VolumeChart canceled due to new request.')
    }
      
    cancelTokenVolumes = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(VOLUMES, true), { cancelToken: cancelTokenVolumes?.token })
      .then(({ data }) => ({
        data: this.model.getMany(data?.data || data),
        ...rest,
      }));
  };
}
export class CircularEconomyChart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';
  model = new CircularGraph();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: CircularChartInstance[];
      materials: MaterialInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenCircularEconomy !== undefined) {
      cancelTokenCircularEconomy.cancel('Operation CircularEconomyChart canceled due to new request.')
    }
      
    cancelTokenCircularEconomy = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(CIRCULAR_ECONOMY, true), { cancelToken: cancelTokenCircularEconomy?.token })
      .then(({ data }) => ({
        data: this.model.getMany(data?.data || data),
        materials: new Material().getMany(data.materials),
        ...rest,
      }));
  };
}

export class MovementVolumeChart extends AbstractBCMPortal {
  endpoint = '/movement';
  model = new VolumeGraphModel();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: MultiGraphInstance[];
      materials: MaterialInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenMovementVolume !== undefined) {
      cancelTokenMovementVolume.cancel('Operation MovementVolumeChart canceled due to new request.')
    }
      
    cancelTokenMovementVolume = axios.CancelToken.source();
    
    return this.request()
      .get(this.getUrl(VOLUMES, true), { cancelToken: cancelTokenMovementVolume?.token })
      .then(({ data }) => ({
        data: this.model.getMany(data?.data || data),
        materials: new Material().getMany(data.materials),
        ...rest,
      }));
  };
}

export class Co2Chart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';
  model = new Co2ChartModel();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: Co2ChartInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenCo2 !== undefined) {
      cancelTokenCo2.cancel('Operation Co2Chart canceled due to new request.')
    }
      
    cancelTokenCo2 = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(CO2, true), { cancelToken: cancelTokenCo2?.token })
      .then(({ data }) => ({
        data: this.model.getMany(data?.data || data),
        ...rest,
      }));
  };
}

export class MovementCo2Chart extends AbstractBCMPortal {
  endpoint = '/movement';
  model = new Co2ChartModel();

  getChart = (
    rest?: ChartRestInterface
  ): Promise<
    {
      data: Co2ChartInstance[];
      materials: MaterialInstance[];
    } & ChartRestInterface
  > => {
    if (cancelTokenMovementCo2 !== undefined) {
      cancelTokenMovementCo2.cancel('Operation MovementCo2Chart canceled due to new request.')
    }
      
    cancelTokenMovementCo2 = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(CO2, true), { cancelToken: cancelTokenMovementCo2?.token })
      .then(({ data }) => ({
        data: this.model.getMany(data?.data || data),
        materials: new Material().getMany(data.materials),
        ...rest,
      }));
  };
}

export class MixedChart extends AbstractBCMPortal {
  endpoint = '/analysis_and_reporting';

  getChart = (): Promise<MixedChartInstance> => {
    if (cancelTokenMixed !== undefined) {
      cancelTokenMixed.cancel('Operation MixedChart canceled due to new request.')
    }
      
    cancelTokenMixed = axios.CancelToken.source();
    return this.request()
      .get(this.getUrl(INDEXES, true), { cancelToken: cancelTokenMixed?.token })
      .then(({ data }) => new MixedChartInstance(data));
  };
}

export class ExportChart extends AbstractBCMPortal {
  dailyEndpoint = '/analysis_and_reporting/export_daily';
  monthlyEndpoint = '/analysis_and_reporting/export_monthly';

  getChart = (): Promise<ExportAllDataInterface> => {
    const dailyRequest = this.request()
      .get(this.getUrl(this.dailyEndpoint, true))
      .then(({ data }) => data as ExportDataInterface[]);

    const monthlyRequest = this.request()
      .get(this.getUrl(this.monthlyEndpoint, true))
      .then(({ data }) => data as ExportDataInterface[]);

    return Promise.all([dailyRequest, monthlyRequest]).then(
      ([detailsData, monthlyData]) => {
        return {
          detailsData,
          monthlyData,
        } as ExportAllDataInterface;
      }
    );
  };
}
export class MovementExportChart extends AbstractBCMPortal {
  endpoint = '/movement/export';

  getChart = (): Promise<ExportMovementDataInterface> => {
    return this.request()
      .get(this.getUrl('', true))
      .then(({ data }) => data as ExportMovementDataInterface);
  };
}
